"use client";

import type { JSX, ReactNode } from "react";
import { useMemo, useState } from "react";
import { Input, Pagination, StatusMessage, Text, View } from "@/components";
import useTable from "@/hooks/useTable";
import { useLayout } from "@/providers";
import type {
  CertificateStatus,
  CourseCertificateRow
} from "@/types/elearning";
import { dateForm } from "@/utils/date";
import { i18nEnumKey } from "@/utils/elearning/i18nEnumKey";

/** Filas por página (paginación client-side). */
const ITEMS_PER_PAGE = 10;

const STATUS_BADGES: Record<CertificateStatus, string> = {
  valid: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  expired:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  revoked: "bg-destructive/10 text-destructive"
};

export interface CertificateListProps {
  /** Filas ya cargadas por la página dueña (el organismo no fetchea). */
  rows: CourseCertificateRow[];
  /**
   * Slot opcional de acción por fila (p. ej. Revocar). Sin él, la tabla es de solo
   * lectura y no se dibuja la columna de acción.
   */
  renderRowAction?: (row: CourseCertificateRow) => ReactNode;
  /** Clave i18n del estado vacío (por defecto "noCertificatesIssued"). */
  emptyTitleKey?: string;
}

/**
 * @name CertificateList
 * @description Lista de certificados emitidos (código, titular, vigencia, estado) con
 * buscador y paginación client-side. Es presentacional: recibe las filas ya cargadas.
 * La acción por fila se inyecta con `renderRowAction` (slot); sin ella la tabla queda
 * read-only — así el mismo organismo sirve en Reportes (solo lectura) y en la gestión
 * del curso (con Revocar). La autorización real de cualquier acción es del servidor.
 * @param rows Certificados a listar.
 * @param renderRowAction Slot opcional de acción por fila.
 * @param emptyTitleKey Clave i18n del estado vacío.
 * @returns Tabla de certificados con buscador y paginación.
 */
const CertificateList = ({
  rows,
  renderRowAction,
  emptyTitleKey = "noCertificatesIssued"
}: CertificateListProps): JSX.Element => {
  const { t } = useLayout();
  const [search, setSearch] = useState("");

  // Filtro por código o titular, memoizado (solo recalcula al cambiar filas o término).
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? rows.filter(
          (row) =>
            row.code.toLowerCase().includes(term) ||
            row.holderName.toLowerCase().includes(term)
        )
      : rows;
  }, [rows, search]);

  const table = useTable(filteredRows, ITEMS_PER_PAGE);
  const showAction = Boolean(renderRowAction);

  return (
    <View className="flex flex-col gap-4">
      <Input
        id="buscar-certificado"
        name="buscar-certificado"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("searchCertificate")}
        className="max-w-xs px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-background text-sm text-foreground"
      />

      {table.totalData === 0 ? (
        <StatusMessage tone="empty" title={t(emptyTitleKey)} />
      ) : (
        <>
          <View className="box rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
            <View type="table" className="w-full text-sm">
              <View type="thead">
                <View
                  type="tr"
                  className="text-left text-xs text-muted-foreground border-b border-slate-100 dark:border-slate-800"
                >
                  <View type="th" className="p-4 font-medium">
                    {t("certificateCode")}
                  </View>
                  <View type="th" className="p-4 font-medium">
                    {t("certificateHolder")}
                  </View>
                  <View type="th" className="p-4 font-medium">
                    {t("validUntil")}
                  </View>
                  <View type="th" className="p-4 font-medium">
                    {t("status")}
                  </View>
                  {showAction && <View type="th" className="p-4 font-medium" />}
                </View>
              </View>
              <View type="tbody">
                {table.tableData.map((row) => (
                  <View
                    key={row.id}
                    type="tr"
                    className="border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                  >
                    <View type="td" className="p-4">
                      <Text
                        type="span"
                        className="font-mono text-xs text-foreground"
                      >
                        {row.code}
                      </Text>
                    </View>
                    <View type="td" className="p-4">
                      <Text type="span" className="text-sm text-foreground">
                        {row.holderName}
                      </Text>
                    </View>
                    <View type="td" className="p-4">
                      <Text
                        type="span"
                        className="text-xs text-muted-foreground"
                      >
                        {dateForm(row.expiresAt, "shortDate")}
                      </Text>
                    </View>
                    <View type="td" className="p-4">
                      <View className="flex flex-col gap-1">
                        <Text
                          type="span"
                          className={`w-fit px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGES[row.status]}`}
                        >
                          {t(i18nEnumKey("certificateStatus", row.status))}
                        </Text>
                        {row.revoked && row.revocationReason && (
                          <Text
                            type="span"
                            className="text-xs text-muted-foreground"
                          >
                            {row.revocationReason}
                          </Text>
                        )}
                      </View>
                    </View>
                    {showAction && (
                      <View type="td" className="p-4 text-right">
                        {renderRowAction?.(row)}
                      </View>
                    )}
                  </View>
                ))}
              </View>
            </View>
          </View>

          {table.totalPages > 1 && (
            <Pagination
              totalPages={table.totalPages}
              currentPage={table.currentPage}
              goToPage={table.paginate}
              nextPage={table.nextPage}
              prevPage={table.prevPage}
              startIndex={table.startIndex}
              endIndex={table.endIndex}
              total={table.totalData}
            />
          )}
        </>
      )}
    </View>
  );
};

export default CertificateList;
